import { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';
import {
  ArrowDownLeft,
  ArrowUpRight,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Paperclip,
  Search,
  Trash2,
  Stethoscope,
  Store,
  TrendingUp,
  TrendingDown,
} from 'lucide-react-native';

import {
  Button,
  CatFishPattern,
  EmptyState,
  SkeletonRows,
  Sheet,
  TextField,
  Tabs,
  type TabItem,
} from '@/components/ui';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import {
  movimientosPorBloque,
  crearMovimiento,
  eliminarMovimiento,
  marcarMovimientoPagado,
  subirComprobantePago,
  CATEGORIAS_INGRESO,
  CATEGORIAS_EGRESO,
  METODOS_PAGO,
} from '@/services/firebase/contabilidad';
import { proveedoresQuery } from '@/services/firebase/proveedores';
import { exportContabilidadExcel, exportContabilidadPDF } from '@/services/export';
import { useAuth, selectIsAdmin, selectIsRecepcion } from '@/stores/authStore';
import { colors, radius, typography } from '@/theme/tokens';
import { formatBs, formatDateTime } from '@/utils/format';
import { categoriaMeta } from '@/utils/categoriasContables';
import type { Bloque, MovimientoContable } from '@/types/domain';

// Fecha local "YYYY-MM-DD" sin corrimiento de zona horaria (toISOString daría UTC).
function hoyLocalStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
// "YYYY-MM-DD" -> Date local: si es hoy usa la hora actual; si no, mediodía local.
// Evita que `new Date("2026-06-09")` (medianoche UTC) se vea como las 20:00 del día
// anterior en Bolivia (UTC-4).
function fechaDesdeInput(str: string): Date {
  const [y, m, d] = String(str).split('-').map(Number);
  if (!y || !m || !d) return new Date(NaN);
  const ahora = new Date();
  if (y === ahora.getFullYear() && m === ahora.getMonth() + 1 && d === ahora.getDate()) return ahora;
  return new Date(y, m - 1, d, 12, 0, 0);
}

interface MovConSaldo extends MovimientoContable {
  saldoAntes: number;
  saldoDespues: number;
}

const BLOQUE_INFO: Record<Bloque, { label: string; descripcion: string; icon: typeof Stethoscope }> = {
  clinica: { label: 'Clínica', descripcion: 'Operación principal de la clínica', icon: Stethoscope },
};

const METODO_LABEL: Record<(typeof METODOS_PAGO)[number], string> = {
  efectivo: 'Efectivo',
  qr: 'QR',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  otro: 'Otro',
};

// Paleta dark CliniCAT: magenta profundo en lugar de azul.
const dark = {
  bg: colors.magenta[950],          // #240414
  panel: colors.magenta[900],       // #3B0820
  panelAlt: '#2D0618',
  border: 'rgba(255,255,255,0.10)',
  text: '#FFF1F8',
  muted: '#E8B6CF',
  green: '#34D399',
  red: '#F87171',
};

export function ContabilidadScreen() {
  const { width } = useWindowDimensions();
  const wide = width >= 920;

  const isAdmin = useAuth(selectIsAdmin);
  const isRecepcion = useAuth(selectIsRecepcion);
  const perfil = useAuth((s) => s.perfil);

  const bloquesPermitidos: Bloque[] = useMemo(
    () => (isAdmin || isRecepcion ? ['clinica'] : []),
    [isAdmin, isRecepcion],
  );

  const [bloqueActivo, setBloqueActivo] = useState<Bloque>(bloquesPermitidos[0] ?? 'clinica');
  useEffect(() => {
    if (!bloquesPermitidos.includes(bloqueActivo) && bloquesPermitidos[0]) {
      setBloqueActivo(bloquesPermitidos[0]);
    }
  }, [bloquesPermitidos, bloqueActivo]);

  const [tipoNuevo, setTipoNuevo] = useState<'ingreso' | 'egreso' | null>(null);
  const [detalleMov, setDetalleMov] = useState<MovConSaldo | null>(null);
  const [pagarMov, setPagarMov] = useState<MovConSaldo | null>(null);
  const [desdeStr, setDesdeStr] = useState('');
  const [hastaStr, setHastaStr] = useState('');
  const [proveedorFiltro, setProveedorFiltro] = useState<string>('');
  const [estadoPagoFiltro, setEstadoPagoFiltro] = useState<'todos' | 'pagado' | 'pendiente'>('todos');

  const proveedoresMov = useFirestoreCollection(proveedoresQuery);

  const q = useMemo(() => movimientosPorBloque(bloqueActivo), [bloqueActivo]);
  const { data, loading } = useFirestoreCollection(q);

  const movimientosConSaldo = useMemo<MovConSaldo[]>(() => {
    if (data.length === 0) return [];
    const asc = [...data].sort((a, b) => a.fecha.toMillis() - b.fecha.toMillis());
    let saldo = 0;
    const conSaldo: MovConSaldo[] = asc.map((m) => {
      const antes = saldo;
      // Pendientes de pago NO afectan el saldo hasta que se paguen.
      const cuenta = (m.estadoPago ?? 'pagado') !== 'pendiente';
      if (cuenta) {
        saldo = m.tipo === 'ingreso' ? saldo + m.monto : saldo - m.monto;
      }
      return { ...m, saldoAntes: antes, saldoDespues: saldo };
    });
    return conSaldo.reverse();
  }, [data]);

  const balanceActual = movimientosConSaldo[0]?.saldoDespues ?? 0;

  // Resumen del mes corriente. Pendientes de pago no entran al total ejecutado.
  const resumenMes = useMemo(() => {
    const ahora = new Date();
    let ingresos = 0;
    let egresos = 0;
    let cantidad = 0;
    let pendienteMonto = 0;
    let pendienteCantidad = 0;
    data.forEach((m) => {
      const f = m.fecha.toDate();
      if (f.getMonth() !== ahora.getMonth() || f.getFullYear() !== ahora.getFullYear()) return;
      cantidad += 1;
      const esPendiente = (m.estadoPago ?? 'pagado') === 'pendiente';
      if (esPendiente) {
        pendienteMonto += m.monto;
        pendienteCantidad += 1;
        return;
      }
      if (m.tipo === 'ingreso') ingresos += m.monto;
      else egresos += m.monto;
    });
    return { ingresos, egresos, balance: ingresos - egresos, cantidad, pendienteMonto, pendienteCantidad };
  }, [data]);

  const filtrados = useMemo(() => {
    const desde = desdeStr ? new Date(desdeStr) : null;
    const hasta = hastaStr
      ? (() => {
          const h = new Date(hastaStr);
          h.setHours(23, 59, 59, 999);
          return h;
        })()
      : null;
    if (!desde && !hasta && !proveedorFiltro && estadoPagoFiltro === 'todos') return movimientosConSaldo;
    return movimientosConSaldo.filter((m) => {
      const f = m.fecha.toDate();
      if (desde && f < desde) return false;
      if (hasta && f > hasta) return false;
      if (proveedorFiltro && m.proveedorId !== proveedorFiltro) return false;
      if (estadoPagoFiltro === 'pagado' && (m.estadoPago ?? 'pagado') !== 'pagado') return false;
      if (estadoPagoFiltro === 'pendiente' && (m.estadoPago ?? 'pagado') !== 'pendiente') return false;
      return true;
    });
  }, [movimientosConSaldo, desdeStr, hastaStr, proveedorFiltro, estadoPagoFiltro]);

  // Para los exports usamos los mismos rangos
  const filtroExportDesde = desdeStr ? new Date(desdeStr) : null;
  const filtroExportHasta = hastaStr
    ? (() => {
        const h = new Date(hastaStr);
        h.setHours(23, 59, 59, 999);
        return h;
      })()
    : null;

  const canCreate = isAdmin;
  const bloqueInfo = BLOQUE_INFO[bloqueActivo];

  const tabItems: TabItem[] = bloquesPermitidos.map((b) => ({
    id: b,
    label: BLOQUE_INFO[b].label,
    icon: BLOQUE_INFO[b].icon,
  }));

  return (
    <ScrollView contentContainerStyle={{ padding: wide ? 24 : 12, gap: 14, paddingBottom: 80 }}>
      {/* Tabs + acciones de exportar a la derecha */}
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        {tabItems.length > 1 ? (
          <Tabs items={tabItems} active={bloqueActivo} onChange={(id) => setBloqueActivo(id as Bloque)} />
        ) : <View />}

        <View style={{ flexDirection: 'row', gap: 8 }}>
          <Button
            label="Excel"
            variant="success"
            size="sm"
            iconLeft={<Download size={14} color={colors.white} />}
            onPress={async () => {
              try {
                await exportContabilidadExcel({
                  bloque: bloqueActivo,
                  movimientos: filtrados,
                  desde: filtroExportDesde ?? undefined,
                  hasta: filtroExportHasta ?? undefined,
                });
              } catch (e: any) {
                alert(e?.message ?? 'No se pudo exportar.');
              }
            }}
          />
          <Button
            label="PDF"
            variant="danger"
            size="sm"
            iconLeft={<FileText size={14} color={colors.white} />}
            onPress={async () => {
              try {
                await exportContabilidadPDF({
                  bloque: bloqueActivo,
                  movimientos: filtrados,
                  desde: filtroExportDesde ?? undefined,
                  hasta: filtroExportHasta ?? undefined,
                });
              } catch (e: any) {
                alert(e?.message ?? 'No se pudo exportar.');
              }
            }}
          />
        </View>
      </View>

      {/* Hero balance — más compacto */}
      <View
        style={{
          padding: wide ? 22 : 18,
          borderRadius: radius['3xl'],
          backgroundColor: colors.magenta[700],
          alignItems: 'center',
          justifyContent: 'center',
          // @ts-ignore web only
          backgroundImage: `radial-gradient(800px 300px at 110% -10%, ${colors.magenta[500]}88, transparent 60%), linear-gradient(135deg, ${colors.magenta[800]} 0%, ${colors.magenta[600]} 100%)`,
          gap: 2,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <CatFishPattern variant="magenta" opacity={0.35} tileSize={320} />
        <Text
          style={{
            color: 'rgba(255,255,255,0.85)',
            fontSize: 11,
            fontWeight: typography.weights.bold,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
          }}
        >
          Gasto total acumulado · {bloqueInfo?.label}
        </Text>
        <Text
          style={{
            color: colors.white,
            fontFamily: typography.display.fontFamily,
            fontSize: wide ? 36 : 28,
            fontWeight: typography.weights.bold,
            letterSpacing: -0.6,
          }}
          numberOfLines={1}
        >
          {formatBs(Math.abs(balanceActual))}
        </Text>
      </View>

      {/* Resumen del mes — solo gastos pagados + deuda */}
      <View style={{ flexDirection: 'row', gap: 10, flexWrap: 'wrap' }}>
        <ResumenCard
          label="Pagado este mes"
          monto={resumenMes.egresos}
          accent={dark.red}
          icon={<TrendingDown size={16} color={dark.red} />}
        />
        <ResumenCard
          label="Por pagar"
          monto={resumenMes.pendienteMonto}
          accent={colors.warning[600]}
          icon={<Clock size={16} color={colors.warning[600]} />}
        />
        <ResumenCard
          label="Promedio por gasto"
          monto={resumenMes.egresos > 0 && resumenMes.cantidad > 0 ? resumenMes.egresos / resumenMes.cantidad : 0}
          accent={colors.magenta[700]}
          icon={<TrendingUp size={16} color={colors.magenta[700]} />}
        />
      </View>

      {/* Toolbar oscura: filtros + acciones de crear */}
      <View
        style={{
          backgroundColor: dark.panel,
          borderRadius: radius['2xl'],
          padding: 14,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
          <DateInput label="Desde" value={desdeStr} onChange={setDesdeStr} />
          <DateInput label="Hasta" value={hastaStr} onChange={setHastaStr} />
          <DarkSelect
            label="Proveedor"
            value={proveedorFiltro}
            onChange={setProveedorFiltro}
            options={[
              { value: '', label: 'Todos' },
              ...proveedoresMov.data.map((p) => ({ value: p.id, label: p.nombre })),
            ]}
          />
          <DarkSelect
            label="Estado"
            value={estadoPagoFiltro}
            onChange={(v) => setEstadoPagoFiltro(v as 'todos' | 'pagado' | 'pendiente')}
            options={[
              { value: 'todos', label: 'Todos' },
              { value: 'pagado', label: 'Pagados' },
              { value: 'pendiente', label: 'Pendientes' },
            ]}
          />
          {(desdeStr || hastaStr || proveedorFiltro || estadoPagoFiltro !== 'todos') ? (
            <Pressable
              onPress={() => {
                setDesdeStr('');
                setHastaStr('');
                setProveedorFiltro('');
                setEstadoPagoFiltro('todos');
              }}
              style={({ hovered }) => ({
                paddingHorizontal: 12,
                height: 34,
                borderRadius: 8,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.08)',
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Text style={{ color: dark.text, fontWeight: typography.weights.bold, fontSize: 11 }}>
                LIMPIAR
              </Text>
            </Pressable>
          ) : null}

          <View style={{ flex: 1 }} />

          {canCreate ? (
            <ToolbarBtn
              label="Nuevo gasto"
              icon={<ArrowUpRight size={14} color={colors.white} />}
              color={dark.red}
              onPress={() => setTipoNuevo('egreso')}
            />
          ) : null}
        </View>
      </View>

      {/* Tabla oscura */}
      <View
        style={{
          backgroundColor: dark.bg,
          borderRadius: radius['2xl'],
          overflow: 'hidden',
        }}
      >
        {wide ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'stretch',
              paddingVertical: 14,
              borderBottomWidth: 1,
              borderBottomColor: dark.border,
            }}
          >
            <DarkHeaderCell width={120} align="center">
              <ColTitle text="Fecha" align="center" />
            </DarkHeaderCell>
            <DarkHeaderCell width={160} align="center">
              <ColTitle text="Categoría" align="center" />
            </DarkHeaderCell>
            <DarkHeaderCell flex={1}>
              <ColTitle text="Detalle" />
            </DarkHeaderCell>
            <DarkHeaderCell width={180} align="center">
              <ColTitle text="Monto" align="center" />
            </DarkHeaderCell>
            <DarkHeaderCell width={90} last>
              <Text> </Text>
            </DarkHeaderCell>
          </View>
        ) : null}

        {loading ? (
          <View style={{ padding: 16 }}>
            <SkeletonRows rows={4} />
          </View>
        ) : filtrados.length === 0 ? (
          <View style={{ padding: 24 }}>
            <EmptyState
              illustration="note"
              title="Sin movimientos"
              message={canCreate ? 'Tocá INGRESO o GASTO para registrar el primero.' : 'Sin registros en este rango.'}
            />
          </View>
        ) : (
          filtrados.map((m, idx) => (
            <FilaMov
              key={m.id}
              mov={m}
              alt={idx % 2 === 1}
              canDelete={isAdmin}
              wide={wide}
              onVerDetalle={() => setDetalleMov(m)}
              onMarcarPagado={() => setPagarMov(m)}
            />
          ))
        )}
      </View>

      <MovimientoSheet
        visible={tipoNuevo !== null}
        tipo={tipoNuevo}
        bloque={bloqueActivo}
        onClose={() => setTipoNuevo(null)}
      />
      <DetalleMovimientoSheet mov={detalleMov} onClose={() => setDetalleMov(null)} />
      <MarcarPagadoSheet mov={pagarMov} onClose={() => setPagarMov(null)} />
    </ScrollView>
  );
}

function AccionRapida({
  label,
  sublabel,
  accent,
  bg,
  icon,
  onPress,
}: {
  label: string;
  sublabel: string;
  accent: string;
  bg: string;
  icon: React.ReactNode;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => ({
        flex: 1,
        minWidth: 220,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: 14,
        borderRadius: radius['2xl'],
        backgroundColor: hovered ? bg : colors.white,
        borderWidth: 1.5,
        borderColor: hovered ? accent : colors.ink[100],
        // @ts-ignore web only
        cursor: 'pointer',
        // @ts-ignore web only
        transitionProperty: 'background-color, border-color',
        // @ts-ignore web only
        transitionDuration: '160ms',
      })}
    >
      <View
        style={{
          width: 44,
          height: 44,
          borderRadius: 14,
          backgroundColor: bg,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </View>
      <View style={{ flex: 1 }}>
        <Text style={{ fontSize: 15, fontWeight: typography.weights.bold, color: accent }}>{label}</Text>
        <Text style={{ fontSize: 12, color: colors.ink[500], marginTop: 1 }}>{sublabel}</Text>
      </View>
    </Pressable>
  );
}

function ResumenCard({
  label,
  monto,
  accent,
  icon,
  signed,
}: {
  label: string;
  monto: number;
  accent: string;
  icon: React.ReactNode;
  signed?: boolean;
}) {
  return (
    <View
      style={{
        flex: 1,
        minWidth: 150,
        padding: 14,
        borderRadius: radius['2xl'],
        backgroundColor: colors.white,
        borderLeftWidth: 3,
        borderLeftColor: accent,
        gap: 6,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        {icon}
        <Text
          style={{
            fontSize: 11,
            color: colors.ink[500],
            fontWeight: typography.weights.bold,
            letterSpacing: 0.8,
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Text>
      </View>
      <Text
        style={{
          fontFamily: typography.display.fontFamily,
          fontSize: 22,
          fontWeight: typography.weights.bold,
          color: accent,
          letterSpacing: -0.3,
        }}
        numberOfLines={1}
      >
        {signed && monto > 0 ? '+' : ''}
        {formatBs(monto)}
      </Text>
    </View>
  );
}

function ColTitle({
  text,
  flex,
  width,
  align,
}: {
  text: string;
  flex?: number;
  width?: number;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <Text
      style={{
        flex,
        width,
        color: dark.muted,
        fontSize: 11,
        fontWeight: typography.weights.bold,
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        textAlign: align ?? 'left',
      }}
    >
      {text}
    </Text>
  );
}

function DarkHeaderCell({
  children,
  flex,
  width,
  align = 'left',
  last,
}: {
  children: React.ReactNode;
  flex?: number;
  width?: number;
  align?: 'left' | 'right' | 'center';
  last?: boolean;
}) {
  return (
    <View
      style={{
        flex,
        width,
        paddingHorizontal: 14,
        justifyContent: 'center',
        alignItems: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
        borderRightWidth: last ? 0 : 1,
        borderRightColor: dark.border,
      }}
    >
      {children}
    </View>
  );
}

// ---- Helper: elegir archivo (imagen/PDF) en web ----
function elegirArchivo(
  onPicked: (f: { blob: Blob; name: string; size: number }) => void,
  onError: (msg: string) => void,
) {
  if (Platform.OS !== 'web') {
    onError('La carga de archivos desde móvil llega pronto.');
    return;
  }
  // @ts-ignore web-only
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*,application/pdf';
  input.onchange = () => {
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      onError('Archivo muy pesado (máx 15 MB).');
      return;
    }
    onPicked({ blob: file, name: file.name, size: file.size });
  };
  input.click();
}

// ---- Selector de archivo reutilizable (caja punteada) ----
function ArchivoPicker({
  label,
  hint,
  file,
  onPick,
}: {
  label: string;
  hint: string;
  file: { name: string; size: number } | null;
  onPick: () => void;
}) {
  return (
    <View style={{ gap: 6 }}>
      <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[700] }}>{label}</Text>
      <Pressable
        onPress={onPick}
        style={({ hovered }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          padding: 14,
          borderRadius: radius.xl,
          borderWidth: 1.5,
          borderStyle: 'dashed',
          borderColor: file ? colors.success[500] : colors.magenta[300],
          backgroundColor: file ? colors.success[50] : hovered ? colors.magenta[50] : colors.crema[50],
          // @ts-ignore web only
          cursor: 'pointer',
        })}
      >
        <FileText size={18} color={file ? colors.success[600] : colors.magenta[700]} />
        <View style={{ flex: 1 }}>
          {file ? (
            <>
              <Text style={{ fontSize: 13, fontWeight: typography.weights.bold, color: colors.success[600] }}>Adjuntado</Text>
              <Text style={{ fontSize: 11, color: colors.ink[500] }} numberOfLines={1}>
                {file.name} · {(file.size / 1024).toFixed(0)} KB
              </Text>
            </>
          ) : (
            <>
              <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[800] }}>{hint}</Text>
              <Text style={{ fontSize: 11, color: colors.ink[500] }}>Imagen o PDF · máx 15 MB</Text>
            </>
          )}
        </View>
        {file ? <Text style={{ color: colors.success[600], fontSize: 11, fontWeight: typography.weights.bold }}>Cambiar</Text> : null}
      </Pressable>
    </View>
  );
}

function AdjuntoLink({ label, url, onOpen }: { label: string; url?: string | null; onOpen: (u?: string | null) => void }) {
  if (!url) {
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, padding: 10, borderRadius: 10, backgroundColor: colors.crema[50] }}>
        <Paperclip size={14} color={colors.ink[300]} />
        <Text style={{ fontSize: 13, color: colors.ink[400] }}>{label}: sin adjunto</Text>
      </View>
    );
  }
  return (
    <Pressable
      onPress={() => onOpen(url)}
      style={({ hovered }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        padding: 10,
        borderRadius: 10,
        backgroundColor: hovered ? colors.magenta[100] : colors.magenta[50],
        // @ts-ignore web only
        cursor: 'pointer',
      })}
    >
      <FileText size={14} color={colors.magenta[700]} />
      <Text style={{ flex: 1, fontSize: 13, fontWeight: typography.weights.bold, color: colors.magenta[700] }}>{label}</Text>
      <Text style={{ fontSize: 12, color: colors.magenta[700], fontWeight: typography.weights.bold }}>Ver</Text>
    </Pressable>
  );
}

// ---- Marcar como pagado (con opción de subir comprobante) ----
function MarcarPagadoSheet({ mov, onClose }: { mov: MovConSaldo | null; onClose: () => void }) {
  const [comprobante, setComprobante] = useState<{ blob: Blob; name: string; size: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mov) return;
    setComprobante(null);
    setError(null);
    setSaving(false);
  }, [mov]);

  if (!mov) return null;

  const onConfirmar = async () => {
    setSaving(true);
    setError(null);
    try {
      let opts: { comprobantePagoUrl?: string | null; comprobantePagoNombre?: string | null } | undefined;
      if (comprobante) {
        const up = await subirComprobantePago(mov.id, comprobante.blob, comprobante.name);
        opts = { comprobantePagoUrl: up.url, comprobantePagoNombre: up.nombre };
      }
      await marcarMovimientoPagado(mov.id, opts);
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo marcar como pagado.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet
      visible={!!mov}
      onClose={onClose}
      size="sm"
      title="Marcar como pagado"
      subtitle={`${mov.descripcion} · ${formatBs(mov.monto)}`}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} disabled={saving} />
          <Button label="Marcar pagado" onPress={onConfirmar} loading={saving} />
        </>
      }
    >
      {mov.proveedorNombre ? (
        <Text style={{ fontSize: 13, color: colors.ink[600] }}>Proveedor: {mov.proveedorNombre}</Text>
      ) : null}
      <ArchivoPicker
        label="Comprobante / Recibo de pago (opcional)"
        hint="Adjuntar el recibo del pago"
        file={comprobante}
        onPick={() => elegirArchivo(setComprobante, setError)}
      />
      <Text style={{ fontSize: 12, color: colors.ink[500] }}>
        Si no tenés el comprobante ahora, podés marcarlo pagado igual.
      </Text>
      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>{error}</Text>
        </View>
      ) : null}
    </Sheet>
  );
}

// ---- Detalle (solo lectura) ----
function DetalleMovimientoSheet({ mov, onClose }: { mov: MovConSaldo | null; onClose: () => void }) {
  if (!mov) return null;
  const esEgreso = mov.tipo === 'egreso';
  const filas: { label: string; valor: string }[] = [
    { label: 'Tipo', valor: esEgreso ? 'Gasto (egreso)' : 'Ingreso' },
    { label: 'Categoría', valor: mov.categoria ?? '—' },
    { label: 'Monto', valor: `${esEgreso ? '−' : '+'} ${formatBs(mov.monto)}` },
    { label: 'Fecha', valor: formatDateTime(mov.fecha.toDate()) },
    { label: 'Método de pago', valor: METODO_LABEL[mov.metodoPago as (typeof METODOS_PAGO)[number]] ?? mov.metodoPago ?? '—' },
    { label: 'Estado', valor: (mov.estadoPago ?? 'pagado') === 'pendiente' ? 'Pendiente de pago' : 'Pagado' },
  ];
  if (mov.proveedorNombre) filas.push({ label: 'Proveedor', valor: mov.proveedorNombre });
  if (mov.numeroFactura) filas.push({ label: 'Nº de factura', valor: mov.numeroFactura });
  if (mov.autorNombre) filas.push({ label: 'Registrado por', valor: mov.autorNombre });

  const abrir = (url?: string | null) => {
    if (url && Platform.OS === 'web') {
      // @ts-ignore web-only
      window.open(url, '_blank');
    }
  };

  return (
    <Sheet
      visible={!!mov}
      onClose={onClose}
      size="md"
      title="Detalle del movimiento"
      subtitle="Solo lectura"
      footer={<Button label="Cerrar" onPress={onClose} />}
    >
      <Text style={{ fontSize: 15, fontWeight: typography.weights.bold, color: colors.ink[900] }}>{mov.descripcion}</Text>
      <View style={{ gap: 0 }}>
        {filas.map((f) => (
          <View key={f.label} style={{ flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.ink[100] }}>
            <Text style={{ fontSize: 13, color: colors.ink[500] }}>{f.label}</Text>
            <Text style={{ fontSize: 13, fontWeight: typography.weights.semibold, color: colors.ink[900], textAlign: 'right', flex: 1 }}>{f.valor}</Text>
          </View>
        ))}
      </View>
      <View style={{ gap: 8 }}>
        <Text style={{ fontSize: 11, color: colors.ink[500], fontWeight: typography.weights.bold, letterSpacing: 1.2, textTransform: 'uppercase' }}>Adjuntos</Text>
        <AdjuntoLink label="Factura / Proforma" url={(mov as any).adjuntoUrl} onOpen={abrir} />
        <AdjuntoLink label="Comprobante / Recibo de pago" url={mov.comprobantePagoUrl} onOpen={abrir} />
      </View>
    </Sheet>
  );
}

function FilaMov({ mov, alt, canDelete, wide, onVerDetalle, onMarcarPagado }: { mov: MovConSaldo; alt: boolean; canDelete: boolean; wide: boolean; onVerDetalle: () => void; onMarcarPagado: () => void }) {
  const fecha = mov.fecha.toDate();
  const positivo = mov.tipo === 'ingreso';
  const dd = String(fecha.getDate()).padStart(2, '0');
  const mm = String(fecha.getMonth() + 1).padStart(2, '0');
  const yyyy = fecha.getFullYear();
  const hh = String(fecha.getHours()).padStart(2, '0');
  const min = String(fecha.getMinutes()).padStart(2, '0');

  // ----- Celular: tarjeta -----
  if (!wide) {
    const pendiente = mov.estadoPago === 'pendiente';
    return (
      <Pressable
        onPress={onVerDetalle}
        style={{
          padding: 14,
          backgroundColor: alt ? dark.panelAlt : dark.bg,
          borderBottomWidth: 1,
          borderBottomColor: dark.border,
          gap: 8,
          // @ts-ignore web only
          cursor: 'pointer',
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <Text style={{ color: dark.text, fontSize: 14, fontWeight: typography.weights.bold, flex: 1 }} numberOfLines={2}>
            {mov.descripcion}
          </Text>
          <Text style={{ fontFamily: typography.display.fontFamily, fontSize: 16, fontWeight: typography.weights.bold, color: positivo ? dark.green : dark.red }}>
            {positivo ? '+' : '−'}{formatBs(mov.monto).replace('Bs ', '')} Bs
          </Text>
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Text style={{ color: dark.muted, fontSize: 12, fontFamily: 'monospace' }}>
            {dd}/{mm}/{yyyy} · {hh}:{min}
          </Text>
          <Text style={{ color: dark.muted, fontSize: 12 }}>· {mov.categoria}</Text>
          {pendiente ? (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 8, paddingVertical: 2, borderRadius: 999, backgroundColor: 'rgba(245,158,11,0.18)', borderWidth: 1, borderColor: 'rgba(245,158,11,0.4)' }}>
              <Clock size={10} color="#FBBF24" />
              <Text style={{ color: '#FBBF24', fontSize: 10, fontWeight: typography.weights.bold, letterSpacing: 0.4, textTransform: 'uppercase' }}>Pendiente</Text>
            </View>
          ) : null}
        </View>
        {canDelete ? (
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end', marginTop: 2 }}>
            {pendiente ? (
              <Pressable
                onPress={(e: any) => { e?.stopPropagation?.(); onMarcarPagado(); }}
                style={({ hovered }) => ({ flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, height: 32, borderRadius: 999, backgroundColor: hovered ? 'rgba(52,211,153,0.28)' : 'rgba(52,211,153,0.14)', borderWidth: 1, borderColor: 'rgba(52,211,153,0.4)', /* @ts-ignore */ cursor: 'pointer' })}
              >
                <CheckCircle2 size={14} color={dark.green} />
                <Text style={{ color: dark.green, fontSize: 12, fontWeight: typography.weights.bold }}>Marcar pagado</Text>
              </Pressable>
            ) : null}
            <Pressable
              onPress={(e: any) => { e?.stopPropagation?.(); eliminarMovimiento(mov.id); }}
              hitSlop={6}
              style={({ hovered }) => ({ width: 32, height: 32, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: hovered ? 'rgba(239,68,68,0.18)' : 'transparent', borderWidth: 1, borderColor: 'rgba(239,68,68,0.4)', /* @ts-ignore */ cursor: 'pointer' })}
            >
              <Trash2 size={14} color={dark.red} />
            </Pressable>
          </View>
        ) : null}
      </Pressable>
    );
  }

  // ----- PC: fila de tabla -----
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'stretch',
        paddingVertical: 14,
        backgroundColor: alt ? dark.panelAlt : dark.bg,
        borderBottomWidth: 1,
        borderBottomColor: dark.border,
      }}
    >
      <Pressable
        onPress={onVerDetalle}
        style={({ hovered }) => ({
          flexDirection: 'row',
          alignItems: 'stretch',
          flex: 1,
          backgroundColor: hovered ? 'rgba(255,255,255,0.04)' : 'transparent',
          // @ts-ignore web only
          cursor: 'pointer',
        })}
      >
      <DarkHeaderCell width={120} align="center">
        <View style={{ alignItems: 'center' }}>
          <Text style={{ color: dark.text, fontSize: 13, fontFamily: 'monospace' }}>
            {dd}/{mm}/{yyyy}
          </Text>
          <Text style={{ color: dark.muted, fontSize: 12, fontFamily: 'monospace', marginTop: 2 }}>
            {hh}:{min}
          </Text>
        </View>
      </DarkHeaderCell>

      <DarkHeaderCell width={160} align="center">
        <Text
          style={{ color: dark.muted, fontSize: 13, fontWeight: typography.weights.semibold, textAlign: 'center' }}
          numberOfLines={2}
        >
          {mov.categoria}
        </Text>
      </DarkHeaderCell>

      <DarkHeaderCell flex={1}>
        <Text
          style={{
            color: dark.text,
            fontSize: 13,
            fontWeight: typography.weights.bold,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}
          numberOfLines={2}
        >
          {mov.descripcion}
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
          {mov.estadoPago === 'pendiente' ? (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                paddingHorizontal: 8,
                paddingVertical: 2,
                borderRadius: 999,
                backgroundColor: 'rgba(245,158,11,0.18)',
                borderWidth: 1,
                borderColor: 'rgba(245,158,11,0.4)',
              }}
            >
              <Clock size={10} color="#FBBF24" />
              <Text style={{ color: '#FBBF24', fontSize: 10, fontWeight: typography.weights.bold, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                Pendiente
              </Text>
            </View>
          ) : null}
          {mov.comprobantePagoUrl ? (
            <Pressable
              onPress={() => {
                if (Platform.OS === 'web') {
                  // @ts-ignore web-only
                  window.open(mov.comprobantePagoUrl, '_blank');
                }
              }}
              style={({ hovered }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: 4,
                paddingHorizontal: 8,
                paddingVertical: 2,
                borderRadius: 999,
                backgroundColor: hovered ? 'rgba(52,211,153,0.28)' : 'rgba(52,211,153,0.14)',
                borderWidth: 1,
                borderColor: 'rgba(52,211,153,0.4)',
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Paperclip size={10} color={dark.green} />
              <Text style={{ color: dark.green, fontSize: 10, fontWeight: typography.weights.bold, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                Ver recibo
              </Text>
            </Pressable>
          ) : null}
          {mov.proveedorNombre || mov.numeroFactura ? (
            <Text style={{ color: dark.muted, fontSize: 11 }} numberOfLines={1}>
              {[mov.proveedorNombre, mov.numeroFactura ? `Fac. ${mov.numeroFactura}` : null]
                .filter(Boolean)
                .join(' · ')}
            </Text>
          ) : null}
        </View>
      </DarkHeaderCell>

      <DarkHeaderCell width={180} align="center">
        <View style={{ alignItems: 'center' }}>
          <Text
            style={{
              fontFamily: typography.display.fontFamily,
              fontSize: 17,
              fontWeight: typography.weights.bold,
              color: positivo ? dark.green : dark.red,
              textAlign: 'center',
            }}
          >
            {positivo ? '+' : '−'}
            {formatBs(mov.monto).replace('Bs ', '')} Bs
          </Text>
          <Text style={{ color: dark.muted, fontSize: 11, fontFamily: 'monospace', marginTop: 2, textAlign: 'center' }}>
            {formatBs(mov.saldoAntes).replace('Bs ', '')} → {formatBs(mov.saldoDespues).replace('Bs ', '')}
          </Text>
        </View>
      </DarkHeaderCell>

      </Pressable>
      <DarkHeaderCell width={90} align="center" last>
        <View style={{ flexDirection: 'row', gap: 4, alignItems: 'center', justifyContent: 'flex-end' }}>
          {canDelete && mov.estadoPago === 'pendiente' ? (
            <Pressable
              onPress={onMarcarPagado}
              hitSlop={6}
              style={({ hovered }) => ({
                width: 28,
                height: 28,
                borderRadius: 8,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? 'rgba(52,211,153,0.25)' : 'rgba(52,211,153,0.12)',
                // @ts-ignore web only
                cursor: 'pointer',
              })}
              accessibilityLabel="Marcar como pagado"
            >
              <CheckCircle2 size={14} color={dark.green} />
            </Pressable>
          ) : null}
          {canDelete ? (
            <Pressable
              onPress={() => eliminarMovimiento(mov.id)}
              hitSlop={6}
              style={({ hovered }) => ({
                width: 28,
                height: 28,
                borderRadius: 8,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: hovered ? 'rgba(239,68,68,0.18)' : 'transparent',
                // @ts-ignore web only
                cursor: 'pointer',
              })}
            >
              <Trash2 size={14} color={dark.red} />
            </Pressable>
          ) : null}
        </View>
      </DarkHeaderCell>
    </View>
  );
}

function ToolbarBtn({
  label,
  icon,
  onPress,
  color,
  outline,
}: {
  label: string;
  icon: React.ReactNode;
  onPress?: () => void;
  color?: string;
  outline?: boolean;
}) {
  return (
    <Pressable
      onPress={onPress}
      style={({ hovered }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        paddingHorizontal: 12,
        height: 34,
        borderRadius: 8,
        backgroundColor: outline
          ? hovered
            ? 'rgba(255,255,255,0.10)'
            : 'rgba(255,255,255,0.05)'
          : hovered
            ? color + 'EE'
            : color,
        borderWidth: outline ? 1 : 0,
        borderColor: 'rgba(255,255,255,0.16)',
        // @ts-ignore web only
        cursor: 'pointer',
      })}
    >
      {icon}
      <Text
        style={{
          color: dark.text,
          fontSize: 11,
          fontWeight: typography.weights.bold,
          letterSpacing: 0.4,
        }}
      >
        {label}
      </Text>
    </Pressable>
  );
}

function DarkSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  if (Platform.OS === 'web') {
    return (
      <View style={{ gap: 3 }}>
        <Text style={{ color: dark.muted, fontSize: 10, fontWeight: typography.weights.bold, letterSpacing: 0.6, textTransform: 'uppercase' }}>
          {label}
        </Text>
        {/* @ts-ignore web-only native select */}
        <select
          value={value}
          onChange={(e) => onChange((e.target as any).value)}
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: `1px solid ${dark.border}`,
            color: dark.text,
            borderRadius: 8,
            padding: '0 10px',
            height: 34,
            fontSize: 12,
            fontFamily: 'inherit',
            colorScheme: 'dark',
            outline: 'none',
            minWidth: 140,
            appearance: 'none',
          }}
        >
          {options.map((o) => (
            // @ts-ignore web-only option
            <option key={o.value} value={o.value} style={{ background: dark.panel, color: dark.text }}>
              {o.label}
            </option>
          ))}
        </select>
      </View>
    );
  }
  return null;
}

function DateInput({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  if (Platform.OS === 'web') {
    return (
      <View style={{ gap: 3 }}>
        <Text style={{ color: dark.muted, fontSize: 10, fontWeight: typography.weights.bold, letterSpacing: 0.6, textTransform: 'uppercase' }}>
          {label}
        </Text>
        {/* @ts-ignore web-only native input */}
        <input
          type="date"
          value={value}
          onChange={(e) => onChange((e.target as any).value)}
          style={{
            background: 'rgba(255,255,255,0.08)',
            border: `1px solid ${dark.border}`,
            color: dark.text,
            borderRadius: 8,
            padding: '0 10px',
            height: 34,
            fontSize: 12,
            fontFamily: 'inherit',
            colorScheme: 'dark',
            outline: 'none',
            width: 140,
          }}
          placeholder="dd/mm/aaaa"
        />
      </View>
    );
  }
  return (
    <View style={{ gap: 3 }}>
      <Text style={{ color: dark.muted, fontSize: 10, fontWeight: typography.weights.bold, textTransform: 'uppercase' }}>{label}</Text>
      <TextField placeholder="YYYY-MM-DD" value={value} onChangeText={onChange} containerStyle={{ width: 140 }} />
    </View>
  );
}

function MovimientoSheet({
  visible,
  tipo,
  bloque,
  onClose,
}: {
  visible: boolean;
  tipo: 'ingreso' | 'egreso' | null;
  bloque: Bloque;
  onClose: () => void;
}) {
  const perfil = useAuth((s) => s.perfil);
  const proveedores = useFirestoreCollection(visible && tipo === 'egreso' ? proveedoresQuery : null);

  const [categoria, setCategoria] = useState<string | null>(null);
  const [monto, setMonto] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [fecha, setFecha] = useState(hoyLocalStr());
  const [metodoPago, setMetodoPago] = useState<(typeof METODOS_PAGO)[number]>('efectivo');
  const [proveedorId, setProveedorId] = useState<string | null>(null);
  const [numeroFactura, setNumeroFactura] = useState('');
  const [estadoPago, setEstadoPago] = useState<'pagado' | 'pendiente'>('pagado');
  const [factura, setFactura] = useState<{ blob: Blob; name: string; size: number } | null>(null);
  const [comprobante, setComprobante] = useState<{ blob: Blob; name: string; size: number } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) return;
    setCategoria(null);
    setMonto('');
    setDescripcion('');
    setFecha(hoyLocalStr());
    setMetodoPago('efectivo');
    setProveedorId(null);
    setNumeroFactura('');
    setEstadoPago('pagado');
    setFactura(null);
    setComprobante(null);
    setError(null);
  }, [visible]);

  const onPickComprobante = () => elegirArchivo(setComprobante, setError);
  const onPickFactura = () => elegirArchivo(setFactura, setError);

  if (!tipo) return null;

  const categorias = (tipo === 'ingreso' ? CATEGORIAS_INGRESO : CATEGORIAS_EGRESO)[bloque];
  const proveedorSeleccionado = proveedores.data.find((p) => p.id === proveedorId) ?? null;
  const esEgreso = tipo === 'egreso';

  const onSave = async () => {
    setError(null);
    if (!perfil) return setError('Sesión inválida.');
    const m = Number(monto.replace(',', '.'));
    if (Number.isNaN(m) || m <= 0) return setError('Monto inválido.');
    if (!descripcion.trim()) return setError('Agregá una descripción.');
    const f = fechaDesdeInput(fecha);
    if (Number.isNaN(f.getTime())) return setError('Fecha inválida.');
    setSaving(true);
    try {
      // Subo los adjuntos si los hay
      const tempId = `mov-${Date.now()}`;
      let adjuntoUrl: string | null = null;
      if (factura) {
        const upF = await subirComprobantePago(`${tempId}-factura`, factura.blob, factura.name);
        adjuntoUrl = upF.url;
      }
      let comprobanteUrl: string | null = null;
      let comprobanteNombre: string | null = null;
      if (comprobante) {
        const up = await subirComprobantePago(tempId, comprobante.blob, comprobante.name);
        comprobanteUrl = up.url;
        comprobanteNombre = up.nombre;
      }

      await crearMovimiento({
        bloque,
        tipo,
        categoria: categoria ?? 'Sin categoría',
        monto: m,
        metodoPago,
        descripcion: descripcion.trim(),
        proveedorId: esEgreso ? proveedorSeleccionado?.id ?? null : null,
        proveedorNombre: esEgreso ? proveedorSeleccionado?.nombre ?? null : null,
        numeroFactura: esEgreso && numeroFactura.trim() ? numeroFactura.trim() : null,
        estadoPago: esEgreso ? estadoPago : 'pagado',
        comprobantePagoUrl: comprobanteUrl,
        comprobantePagoNombre: comprobanteNombre,
        adjuntoUrl,
        fecha: f,
        autorUid: perfil.uid,
        autorNombre: perfil.nombre,
      });
      onClose();
    } catch (e: any) {
      setError(e?.message ?? 'No se pudo guardar.');
    } finally {
      setSaving(false);
    }
  };

  const accentColor = esEgreso ? colors.danger[600] : colors.success[600];

  return (
    <Sheet
      visible={visible}
      onClose={onClose}
      size="lg"
      title={esEgreso ? 'Registrar gasto' : 'Agregar ingreso'}
      subtitle={`${BLOQUE_INFO[bloque].label} · ${BLOQUE_INFO[bloque].descripcion}`}
      footer={
        <>
          <Button label="Cancelar" variant="ghost" onPress={onClose} />
          <Button
            label={esEgreso ? 'Registrar gasto' : 'Agregar ingreso'}
            onPress={onSave}
            loading={saving}
            variant={esEgreso ? 'danger' : 'success'}
          />
        </>
      }
    >
      {/* Monto destacado con bordes marcados */}
      <View
        style={{
          padding: 18,
          borderRadius: radius['2xl'],
          backgroundColor: colors.white,
          alignItems: 'center',
          gap: 4,
          borderWidth: 2,
          borderColor: accentColor,
          // @ts-ignore web only
          boxShadow: `0 0 0 4px ${accentColor}1A`,
        }}
      >
        <Text
          style={{
            fontSize: 10,
            color: accentColor,
            fontWeight: typography.weights.bold,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
          }}
        >
          Monto
        </Text>
        <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 6 }}>
          <Text style={{ fontSize: 22, fontWeight: typography.weights.bold, color: accentColor }}>Bs</Text>
          {/* @ts-ignore web-only native input */}
          <input
            type="text"
            inputMode="decimal"
            value={monto}
            onChange={(e: any) => {
              const cleaned = String(e.target.value).replace(/[^0-9.,]/g, '').replace(',', '.');
              const parts = cleaned.split('.');
              if (parts.length > 2) return;
              if (parts[1] && parts[1].length > 2) return;
              setMonto(cleaned);
            }}
            placeholder="0.00"
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontSize: 40,
              fontWeight: 800,
              fontFamily: 'inherit',
              color: accentColor,
              textAlign: 'center',
              minWidth: 160,
              padding: 0,
            }}
          />
        </View>
      </View>

      {/* Categorías - grid visual */}
      <View style={{ gap: 8 }}>
        <Text
          style={{
            fontSize: 11,
            color: colors.ink[500],
            fontWeight: typography.weights.bold,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
          }}
        >
          Categoría
        </Text>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          {categorias.map((c) => {
            const meta = categoriaMeta(c);
            const Icon = meta.icon;
            const selected = c === categoria;
            return (
              <Pressable
                key={c}
                onPress={() => setCategoria(c)}
                style={({ hovered }) => ({
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingHorizontal: 12,
                  paddingVertical: 10,
                  borderRadius: radius.xl,
                  backgroundColor: selected ? meta.bgColor : hovered ? colors.crema[50] : colors.white,
                  borderWidth: 1.5,
                  borderColor: selected ? meta.color : colors.ink[100],
                  // @ts-ignore web only
                  cursor: 'pointer',
                  // @ts-ignore web only
                  transitionProperty: 'background-color, border-color',
                  // @ts-ignore web only
                  transitionDuration: '160ms',
                })}
              >
                <Icon size={16} color={selected ? meta.color : colors.ink[500]} />
                <Text
                  style={{
                    fontSize: 13,
                    fontWeight: selected ? typography.weights.bold : typography.weights.medium,
                    color: selected ? meta.color : colors.ink[700],
                  }}
                >
                  {c}
                </Text>
              </Pressable>
            );
          })}
        </View>
        {!(categoria && categorias.includes(categoria as any)) ? (
          <TextField
            placeholder="O escribí una categoría personalizada"
            value={categoria && !categorias.includes(categoria as any) ? categoria : ''}
            onChangeText={(t) => setCategoria(t.trim() || null)}
          />
        ) : null}
      </View>

      <TextField
        label="Detalle"
        placeholder={esEgreso ? 'Ej: Compra de jeringas, pago de luz' : 'Ej: Consulta general · paciente Mishi'}
        value={descripcion}
        onChangeText={setDescripcion}
      />

      {/* Fecha + Método de pago en una sola fila */}
      <View style={{ flexDirection: 'row', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <View style={{ width: 170 }}>
          <Text
            style={{
              fontSize: 11,
              color: colors.ink[500],
              fontWeight: typography.weights.bold,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Fecha
          </Text>
          {Platform.OS === 'web' ? (
            // @ts-ignore web-only native input
            <input
              type="date"
              value={fecha}
              onChange={(e: any) => setFecha(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 12,
                border: `1px solid ${colors.ink[200]}`,
                background: colors.white,
                color: colors.ink[900],
                fontSize: 14,
                fontFamily: 'inherit',
                outline: 'none',
                height: 42,
                boxSizing: 'border-box',
              }}
            />
          ) : (
            <TextField placeholder="YYYY-MM-DD" value={fecha} onChangeText={setFecha} />
          )}
        </View>
        <View style={{ flex: 1, minWidth: 200, gap: 6 }}>
          <Text
            style={{
              fontSize: 11,
              color: colors.ink[500],
              fontWeight: typography.weights.bold,
              letterSpacing: 1.4,
              textTransform: 'uppercase',
            }}
          >
            Método de pago
          </Text>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
            {METODOS_PAGO.map((m) => {
              const selected = metodoPago === m;
              return (
                <Pressable
                  key={m}
                  onPress={() => setMetodoPago(m)}
                  style={({ hovered }) => ({
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    borderRadius: 999,
                    backgroundColor: selected ? colors.magenta[700] : hovered ? colors.crema[50] : colors.white,
                    borderWidth: 1,
                    borderColor: selected ? colors.magenta[700] : colors.ink[100],
                    // @ts-ignore web only
                    cursor: 'pointer',
                  })}
                >
                  <Text
                    style={{
                      fontSize: 12,
                      fontWeight: typography.weights.bold,
                      color: selected ? colors.white : colors.ink[700],
                    }}
                  >
                    {METODO_LABEL[m]}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>
      </View>

      {/* Solo para egresos: Proveedor + N° factura */}
      {esEgreso ? (
        <>
          <View style={{ gap: 8 }}>
            <Text
              style={{
                fontSize: 11,
                color: colors.ink[500],
                fontWeight: typography.weights.bold,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
              }}
            >
              Proveedor (opcional)
            </Text>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
              <Pressable
                onPress={() => setProveedorId(null)}
                style={({ hovered }) => ({
                  paddingHorizontal: 12,
                  paddingVertical: 8,
                  borderRadius: 999,
                  backgroundColor: !proveedorId ? colors.ink[100] : hovered ? colors.crema[50] : colors.white,
                  borderWidth: 1,
                  borderColor: !proveedorId ? colors.ink[300] : colors.ink[100],
                  // @ts-ignore web only
                  cursor: 'pointer',
                })}
              >
                <Text
                  style={{ fontSize: 12, fontWeight: typography.weights.bold, color: colors.ink[600] }}
                >
                  Sin proveedor
                </Text>
              </Pressable>
              {proveedores.data.map((p) => {
                const selected = proveedorId === p.id;
                return (
                  <Pressable
                    key={p.id}
                    onPress={() => setProveedorId(p.id)}
                    style={({ hovered }) => ({
                      paddingHorizontal: 12,
                      paddingVertical: 8,
                      borderRadius: 999,
                      backgroundColor: selected ? colors.magenta[700] : hovered ? colors.crema[50] : colors.white,
                      borderWidth: 1,
                      borderColor: selected ? colors.magenta[700] : colors.ink[100],
                      // @ts-ignore web only
                      cursor: 'pointer',
                    })}
                  >
                    <Text
                      style={{
                        fontSize: 12,
                        fontWeight: typography.weights.bold,
                        color: selected ? colors.white : colors.ink[700],
                      }}
                    >
                      {p.nombre}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </View>

          <TextField
            label="N° factura / recibo (opcional)"
            placeholder="Ej: 001-00234"
            value={numeroFactura}
            onChangeText={setNumeroFactura}
          />

          {/* Estado del pago */}
          <View style={{ gap: 8 }}>
            <Text
              style={{
                fontSize: 11,
                color: colors.ink[500],
                fontWeight: typography.weights.bold,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
              }}
            >
              Estado del pago
            </Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {(['pagado', 'pendiente'] as const).map((opt) => {
                const active = estadoPago === opt;
                const isPagado = opt === 'pagado';
                return (
                  <Pressable
                    key={opt}
                    onPress={() => setEstadoPago(opt)}
                    style={({ hovered }) => ({
                      flex: 1,
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      paddingVertical: 12,
                      borderRadius: radius.xl,
                      backgroundColor: active
                        ? isPagado
                          ? colors.success[50]
                          : colors.warning[50]
                        : hovered
                          ? colors.crema[50]
                          : colors.white,
                      borderWidth: 1.5,
                      borderColor: active
                        ? isPagado
                          ? colors.success[500]
                          : colors.warning[500]
                        : colors.ink[200],
                      // @ts-ignore web only
                      cursor: 'pointer',
                    })}
                  >
                    <Text
                      style={{
                        fontSize: 14,
                        fontWeight: typography.weights.bold,
                        color: active
                          ? isPagado
                            ? colors.success[600]
                            : colors.warning[600]
                          : colors.ink[700],
                      }}
                    >
                      {isPagado ? 'Ya pagado' : 'Pendiente de pago'}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
            <Text style={{ fontSize: 12, color: colors.ink[500] }}>
              {estadoPago === 'pagado'
                ? 'El gasto se registra como ya cancelado al proveedor.'
                : 'Se registra ahora, lo marcás como pagado cuando le pagues.'}
            </Text>
          </View>

          {/* Adjuntos: factura/proforma + comprobante/recibo */}
          <ArchivoPicker
            label="Factura / Proforma (opcional)"
            hint="Adjuntar la factura o proforma"
            file={factura}
            onPick={onPickFactura}
          />
          <ArchivoPicker
            label="Comprobante / Recibo de pago (opcional)"
            hint="Adjuntar el recibo del pago"
            file={comprobante}
            onPick={onPickComprobante}
          />
        </>
      ) : null}

      {error ? (
        <View style={{ padding: 12, borderRadius: 12, backgroundColor: colors.danger[50] }}>
          <Text style={{ color: colors.danger[600], fontSize: 13, fontWeight: typography.weights.semibold }}>
            {error}
          </Text>
        </View>
      ) : null}
    </Sheet>
  );
}
